// src/pages/Reps.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, query, orderBy } from 'firebase/firestore';
import { createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { db, auth } from '../firebase';
import { ROLES, DEFAULT_COMP_PLAN } from '../data/compPlans';

const EMPTY_FORM = { name: '', email: '', role: 'Agent', compPlanId: 'synergy_1_31_26', phone: '', password: '', agentCode: '', managerId: '' };

// Roles eligible to be a manager (can have downline reports)
const MANAGER_ROLES = ['Lead', 'Manager', 'Regional', 'Asst Recruiter', 'Recruiter', 'Director', 'admin'];

export default function Reps() {
  const [reps, setReps] = useState([]);
  const [compPlans, setCompPlans] = useState([DEFAULT_COMP_PLAN]);
  const [showForm, setShowForm] = useState(false);
  const [editingRep, setEditingRep] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showBulkAssign, setShowBulkAssign] = useState(false);
  const [bulkPlanId, setBulkPlanId] = useState('synergy_1_31_26');
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkSearch, setBulkSearch] = useState('');

  useEffect(() => {
    async function load() {
      const [repSnap, planSnap] = await Promise.all([
        getDocs(query(collection(db, 'users'), orderBy('name'))),
        getDocs(collection(db, 'compPlans')),
      ]);
      setReps(repSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const dbPlans = planSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (dbPlans.length) setCompPlans([DEFAULT_COMP_PLAN, ...dbPlans]);
    }
    load();
  }, []);

  function startEdit(rep) {
    setEditingRep(rep);
    setForm({ name: rep.name || '', email: rep.email || '', role: rep.role || 'Agent', compPlanId: rep.compPlanId || 'synergy_1_31_26', phone: rep.phone || '', agentCode: rep.agentCode || '', password: '', managerId: rep.managerId || '' });
    setShowForm(true);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function cancelForm() {
    setShowForm(false);
    setEditingRep(null);
    setForm(EMPTY_FORM);
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (editingRep) {
        // Update existing rep
        const updates = { name: form.name, role: form.role, compPlanId: form.compPlanId, phone: form.phone, agentCode: form.agentCode, terminationDate: form.terminationDate || null, managerId: form.managerId || null, needsAgentCode: form.agentCode ? false : (editingRep.needsAgentCode || false) };
        await updateDoc(doc(db, 'users', editingRep.id), updates);
        setReps(prev => prev.map(r => r.id === editingRep.id ? { ...r, ...updates } : r));
      } else {
        // Create new rep
        const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);
        const profile = { name: form.name, email: form.email, role: form.role, compPlanId: form.compPlanId, phone: form.phone, agentCode: form.agentCode, uid: cred.user.uid, active: true, managerId: form.managerId || null };
        await addDoc(collection(db, 'users'), profile);
        setReps(prev => [...prev, { id: cred.user.uid, ...profile }]);
      }
      cancelForm();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  }

  async function resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
      alert(`Password reset email sent to ${email}`);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  async function toggleActive(id, current) {
    await updateDoc(doc(db, 'users', id), { active: !current });
    setReps(prev => prev.map(r => r.id === id ? { ...r, active: !current } : r));
  }

  function openBulkAssign() {
    setBulkSelected({});
    setBulkPlanId(compPlans[0]?.id || 'synergy_1_31_26');
    setBulkSearch('');
    setShowBulkAssign(true);
  }

  function toggleBulkSelect(id) {
    setBulkSelected(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleBulkAll(repsList) {
    const allSelected = repsList.every(r => bulkSelected[r.id]);
    const next = { ...bulkSelected };
    repsList.forEach(r => { next[r.id] = !allSelected; });
    setBulkSelected(next);
  }

  async function handleBulkAssign() {
    const ids = Object.entries(bulkSelected).filter(([, v]) => v).map(([id]) => id);
    if (ids.length === 0) return;
    setBulkSaving(true);
    try {
      await Promise.all(ids.map(id => updateDoc(doc(db, 'users', id), { compPlanId: bulkPlanId })));
      setReps(prev => prev.map(r => ids.includes(r.id) ? { ...r, compPlanId: bulkPlanId } : r));
      setShowBulkAssign(false);
    } catch (err) {
      alert('Error: ' + err.message);
    }
    setBulkSaving(false);
  }

  const bulkFilteredReps = reps.filter(r => r.role !== 'admin' && r.active !== false).filter(r => !bulkSearch || r.name?.toLowerCase().includes(bulkSearch.toLowerCase()) || r.email?.toLowerCase().includes(bulkSearch.toLowerCase()) || r.agentCode?.toLowerCase().includes(bulkSearch.toLowerCase()));
  const bulkSelectedCount = Object.values(bulkSelected).filter(Boolean).length;

  const filtered = reps.filter(r => !search || r.name?.toLowerCase().includes(search.toLowerCase()) || r.email?.toLowerCase().includes(search.toLowerCase()) || r.agentCode?.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reps</h1>
          <p style={styles.sub}>{reps.filter(r => r.active !== false).length} active · {reps.length} total</p>
        </div>
        {!showForm && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={styles.bulkAssignBtn} onClick={openBulkAssign}>Bulk assign plan</button>
            <button style={styles.addBtn} onClick={() => { setEditingRep(null); setForm(EMPTY_FORM); setShowForm(true); }}>+ Add rep</button>
          </div>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.formCard}>
          <div style={styles.formTitleRow}>
            <div style={styles.formTitle}>{editingRep ? `Edit — ${editingRep.name}` : 'New rep'}</div>
            <button type="button" style={styles.cancelBtn} onClick={cancelForm}>✕ Cancel</button>
          </div>
          {error && <div style={styles.error}>{error}</div>}
          <div className="form-grid" style={styles.formGrid}>
            <Field label="Full name"><input style={styles.input} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></Field>
            <Field label="Email"><input style={styles.input} type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required={!editingRep} disabled={!!editingRep} /></Field>
            {!editingRep && <Field label="Password (temp)"><input style={styles.input} type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required minLength={6} /></Field>}
            <Field label="Phone"><input style={styles.input} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></Field>
            <Field label="Agent code (from CSV)"><input style={styles.input} value={form.agentCode} placeholder="e.g. JH003" onChange={e => setForm(f => ({ ...f, agentCode: e.target.value }))} /></Field>
            <Field label="Termination date (if applicable)"><input style={styles.input} type="date" value={form.terminationDate || ''} onChange={e => setForm(f => ({ ...f, terminationDate: e.target.value }))} /></Field>
            <Field label="Role">
              <select style={styles.input} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                {ROLES.map(r => <option key={r}>{r}</option>)}
                <option>ISO</option>
                <option>admin</option>
              </select>
            </Field>
            <Field label="Comp plan">
              <select style={styles.input} value={form.compPlanId} onChange={e => setForm(f => ({ ...f, compPlanId: e.target.value }))}>
                {compPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Manager (upline)">
              <select style={styles.input} value={form.managerId} onChange={e => setForm(f => ({ ...f, managerId: e.target.value }))}>
                <option value="">— None —</option>
                {reps
                  .filter(r => MANAGER_ROLES.includes(r.role) && r.id !== editingRep?.id && r.active !== false)
                  .map(r => <option key={r.id} value={r.uid || r.id}>{r.name} ({r.role})</option>)}
              </select>
            </Field>
          </div>
          <button type="submit" style={styles.submitBtn} disabled={saving}>
            {saving ? 'Saving...' : editingRep ? 'Save changes' : 'Create rep'}
          </button>
        </form>
      )}

      {showBulkAssign && (
        <div style={styles.formCard}>
          <div style={styles.formTitleRow}>
            <div style={styles.formTitle}>Bulk assign comp plan</div>
            <button type="button" style={styles.cancelBtn} onClick={() => setShowBulkAssign(false)}>✕ Cancel</button>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#666', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>Comp plan</label>
              <select style={styles.input} value={bulkPlanId} onChange={e => setBulkPlanId(e.target.value)}>
                {compPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <input style={styles.input} placeholder="Search reps..." value={bulkSearch} onChange={e => setBulkSearch(e.target.value)} />
            </div>
            <button style={{ ...styles.submitBtn, opacity: bulkSelectedCount === 0 ? 0.4 : 1 }} disabled={bulkSaving || bulkSelectedCount === 0} onClick={handleBulkAssign}>
              {bulkSaving ? 'Assigning...' : `Assign ${bulkSelectedCount} rep${bulkSelectedCount !== 1 ? 's' : ''}`}
            </button>
          </div>
          <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#888', cursor: 'pointer' }}>
              <input type="checkbox" checked={bulkFilteredReps.length > 0 && bulkFilteredReps.every(r => bulkSelected[r.id])} onChange={() => toggleBulkAll(bulkFilteredReps)} />
              Select all ({bulkFilteredReps.length})
            </label>
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #1e1e2a', borderRadius: 8 }}>
            {bulkFilteredReps.map(rep => (
              <label key={rep.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #111', cursor: 'pointer', background: bulkSelected[rep.id] ? '#1a2e24' : 'transparent' }}>
                <input type="checkbox" checked={!!bulkSelected[rep.id]} onChange={() => toggleBulkSelect(rep.id)} />
                <span style={{ fontSize: 13, color: '#fff', flex: 1 }}>{rep.name}</span>
                <span style={{ fontSize: 11, color: '#555' }}>{rep.role || 'Agent'}</span>
                <span style={{ fontSize: 11, color: '#444' }}>{compPlans.find(p => p.id === rep.compPlanId)?.name || 'Default plan'}</span>
              </label>
            ))}
            {bulkFilteredReps.length === 0 && (
              <div style={{ padding: 20, textAlign: 'center', color: '#444', fontSize: 13 }}>No active reps found</div>
            )}
          </div>
        </div>
      )}

      <div style={styles.toolbar}>
        <input style={{ ...styles.input, width: 240 }} placeholder="Search by name, email or agent code..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div style={styles.grid}>
        {filtered.map(rep => (
          <div key={rep.id} style={{
            ...styles.repCard,
            opacity: rep.active === false ? 0.5 : 1,
            border: rep.needsAgentCode ? '1px solid #f5a623' : styles.repCard.border,
            background: rep.needsAgentCode ? '#1a1610' : styles.repCard.background,
          }}>
            <div style={styles.repHeader}>
              <div style={styles.avatar}>{(rep.name || 'U')[0].toUpperCase()}</div>
              <div style={{ flex: 1 }}>
                <div style={styles.repName}>{rep.name}</div>
                <div style={styles.repEmail}>{rep.email}</div>
              </div>
              <div style={{ ...styles.roleBadge }}>{rep.role || 'Agent'}</div>
            </div>
            <div style={styles.repMeta}>
              {rep.agentCode && <span style={styles.metaChip}>Code: {rep.agentCode}</span>}
              {rep.needsAgentCode && <span style={{ ...styles.metaChip, background: '#2a2010', color: '#f5a623', border: '1px solid #f5a623' }}>Needs sales ID</span>}
              {rep.phone && <span style={styles.metaChip}>{rep.phone}</span>}
              <span style={styles.metaChip}>{compPlans.find(p => p.id === rep.compPlanId)?.name || 'Default plan'}</span>
            </div>
            <div style={styles.repActions}>
              <button style={styles.editBtn} onClick={() => startEdit(rep)}>Edit</button>
              <button style={styles.resetBtn} onClick={() => resetPassword(rep.email)}>Reset password</button>
              <button style={styles.toggleBtn} onClick={() => toggleActive(rep.id, rep.active !== false)}>
                {rep.active === false ? 'Reactivate' : 'Deactivate'}
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ gridColumn: '1/-1', color: '#444', fontSize: 13, textAlign: 'center', padding: 40 }}>
            No reps found
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, color: '#666', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

const styles = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 13, color: '#555', margin: '4px 0 0' },
  addBtn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '9px 18px', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  bulkAssignBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 8, padding: '9px 18px', color: '#888', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  formCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '24px', marginBottom: 20 },
  formTitleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  formTitle: { fontSize: 14, fontWeight: 600 },
  cancelBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 7, padding: '5px 12px', color: '#666', fontSize: 12, cursor: 'pointer' },
  error: { background: '#2a1515', border: '1px solid #5c2020', borderRadius: 8, padding: '10px 14px', color: '#ff6b6b', fontSize: 13, marginBottom: 14 },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 16 },
  input: { width: '100%', background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  submitBtn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '10px 24px', color: '#000', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  toolbar: { marginBottom: 16 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 },
  repCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '16px 18px' },
  repHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 },
  avatar: { width: 36, height: 36, borderRadius: '50%', background: '#1e2a24', border: '1px solid #00d4a0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 600, color: '#00d4a0', flexShrink: 0 },
  repName: { fontSize: 14, fontWeight: 500, color: '#fff' },
  repEmail: { fontSize: 11, color: '#555' },
  roleBadge: { fontSize: 10, fontWeight: 500, padding: '3px 8px', borderRadius: 5, background: '#1a1a28', color: '#666', textTransform: 'uppercase', letterSpacing: '0.04em' },
  repMeta: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 },
  metaChip: { fontSize: 11, color: '#555', background: '#0f0f18', padding: '3px 8px', borderRadius: 5 },
  repActions: { display: 'flex', gap: 8 },
  editBtn: { background: '#1a2e24', border: '1px solid #2a5a3a', borderRadius: 7, padding: '5px 12px', color: '#00d4a0', fontSize: 11, cursor: 'pointer', fontWeight: 500 },
  resetBtn: { background: '#1a1a2e', border: '1px solid #2a2a5a', borderRadius: 7, padding: '5px 12px', color: '#5599cc', fontSize: 11, cursor: 'pointer', fontWeight: 500 },
  toggleBtn: { background: 'transparent', border: '1px solid #2a2a3a', borderRadius: 7, padding: '5px 12px', color: '#555', fontSize: 11, cursor: 'pointer' },
};
